/**
 * Lens prescribing: the patient's spectacle and contact lens prescriptions.
 *
 * Prescriptions are kept by the REST API (LensPrescriptionAPI) and read here into the shape the
 * prescription dialogs edit. Prescribing owns the clinical decisions - the Rx, and for contact lenses
 * the lens, its powers and the fit. A draft can be changed or deleted; an issued prescription is
 * locked, and dispensing jobs are made from it while it is in date: the job keeps its own copy (see
 * applyToJob), so a prescription written later never changes an order already sent.
 *
 * Every prescription has notes for the dispenser (advice), shown on its dispensing jobs, and notes
 * for the patient, printed on the patient's copy - which the server makes and files in their
 * documents when it is issued. A spectacle prescription's lens recommendations (use, form, material,
 * treatments) are printed too, as the Optometry Board of Australia's guideline asks.
 *
 * An issued prescription found to be wrong is withdrawn, with a reason: it is kept, but can no longer
 * be dispensed. The server sets the prescriber - whoever issues it - and their registration number.
 *
 * A contact lens prescription can go home on trial first, as often as it takes. Every trial is kept with
 * the draft (`fitting.trials`, oldest first) and written in its own dialog; the draft is on trial until
 * the latest trial's review finds a good fit.
 */
app.service("LensPrescribingStore", [
	"$q",
	"$rootScope",
	"LensDispensingStore",
	"LensPrescriptionAPI",
	"Session",
	function ($q, $rootScope, Store, LensPrescriptionAPI, Session) {
		// Spectacle Rx are good for two years, contact lens Rx for one.
		const VALID_DAYS = { glasses: 730, contacts: 365 };
		// The Rx fields a spectacle prescription carries, which a dispensing job copies and never changes.
		const RX_FIELDS = ["sph", "cyl", "axis", "intAdd", "add", "hPrism", "hBase", "vPrism", "vBase", "balance"];
		// The monocular PDs (and, on older prescriptions, fitting heights) the prescriber recorded. A job
		// starts from them, but they depend on the frame, so the dispenser measures them again and the
		// job keeps its own.
		const MEASURE_FIELDS = ["pd", "height"];

		const STATUS = {
			DRAFT: { key: "draft", label: "Draft", icon: "fa-pencil" },
			TRIAL: { key: "trial", label: "On trial", icon: "fa-eye" },
			CURRENT: { key: "current", label: "Current", icon: "fa-check-circle" },
			SUPERSEDED: { key: "superseded", label: "Superseded", icon: "fa-history" },
			EXPIRED: { key: "expired", label: "Expired", icon: "fa-calendar-times-o" },
			WITHDRAWN: { key: "withdrawn", label: "Withdrawn", icon: "fa-ban" },
		};

		const isNum = (v) => v !== null && v !== undefined && v !== "" && !isNaN(v);
		const isContactsRx = (p) => !!p && p.type === "contacts";
		// Issued and not withdrawn: a prescription that stands.
		const isIssued = (p) => !!p && p.status === "ISSUED";
		const isWithdrawn = (p) => !!p && p.status === "WITHDRAWN";
		// Still being written: the only kind that can be changed or deleted.
		const isDraft = (p) => !!p && p.status === "DRAFT";
		const rxOf = (p) => (isContactsRx(p) ? p.cl : p.rx);
		const expiresOf = (p) => (p ? rxOf(p).expires : null);
		// Powers for each eye - a balance eye needs none, but not both eyes can be balance.
		const hasPowers = (rx) => !!rx && ["od", "os"].every((k) => rx[k].balance || isNum(rx[k].sph)) && !(rx.od.balance && rx.os.balance);

		function isExpired(p) {
			const expires = expiresOf(p);
			if (!expires) return false;
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			return new Date(expires) < today;
		}

		// Issued and in date: what a dispensing job can be made from.
		const isUsable = (p) => isIssued(p) && !isExpired(p);

		// What the review of a trial found. Only a good fit lets the prescription be issued.
		const TRIAL_OUTCOMES = [
			{ key: "good", label: "Good fit - prescribe", icon: "fa-check-circle" },
			{ key: "adjust", label: "Adjust power", icon: "fa-sliders" },
			{ key: "refit", label: "Try another lens", icon: "fa-refresh" },
		];
		const outcomeLabel = (key) => (TRIAL_OUTCOMES.find((o) => o.key === key) || {}).label || "-";
		// A contact lens draft whose trial lenses have gone home and not yet settled into a good fit.
		const onTrial = (p) => isContactsRx(p) && isDraft(p) && !!p.fitting && !!p.fitting.trialIssued && p.fitting.outcome !== "good";

		// What a trial's review records of each eye's fit, beside the over-refraction and VA.
		const TRIAL_FIT = {
			centration: [{ key: "centred", label: "Centred" }, { key: "decentred", label: "Decentred" }],
			movement: [{ key: "good", label: "Good" }, { key: "tight", label: "Tight" }, { key: "loose", label: "Loose" }],
			comfort: [{ key: "good", label: "Good" }, { key: "fair", label: "Fair" }, { key: "poor", label: "Poor" }],
			rotation: [{ key: "left", label: "Left" }, { key: "right", label: "Right" }],
		};
		const fitLabel = (kind, key) => (TRIAL_FIT[kind].find((o) => o.key === key) || {}).label || "";

		// The trial a draft is on now - the latest - or null.
		const currentTrial = (p) => (p && p.fitting && p.fitting.trials && p.fitting.trials.length ? p.fitting.trials[p.fitting.trials.length - 1] : null);

		// A new trial pair of what the draft prescribes now, going home today to be reviewed in a week. Its
		// lens and powers are kept as they went, so a later change to the draft never rewrites the trial.
		function newTrial(p) {
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			const lens = (k) => {
				const l = Store.lensFor(p, k);
				return p.cl[k].noLens || !l ? null : { id: l.id || null, name: `${l.brand || ""} ${l.name || ""}`.trim() };
			};
			const rx = (k) => {
				const e = p.cl[k];
				return e.noLens ? { noLens: true } : { sph: e.sph, cyl: e.cyl || null, axis: e.cyl ? e.axis : null, add: e.add || null, bc: e.bc, dia: e.dia, noLens: false };
			};
			const findings = () => ({ orSph: null, orCyl: null, orAxis: null, va: "", centration: null, movement: null, rotation: null, rotationDir: null, comfort: null });
			return {
				sent: today, review: Store.addDays(today, 7), lens: { od: lens("od"), os: lens("os") }, rx: { od: rx("od"), os: rx("os") },
				findings: { od: findings(), os: findings() }, notes: "", outcome: null,
			};
		}

		// The powers a trial's review points to for one eye's next pair: the over-refraction's sphere added -
		// vertex-corrected over 4D, onto the powers lenses are made in - and a toric's axis turned by the
		// rotation seen, by LARS (left add, right subtract, as the examiner sees it), to the 10° a stock toric
		// comes in unless `exactAxis`. A residual cylinder is recorded, not added. Null for an eye with no lens.
		function adjustedRx(trial, eye, exactAxis) {
			const rx = trial.rx[eye];
			const f = trial.findings[eye];
			if (!rx || rx.noLens || !isNum(rx.sph)) return null;
			const out = { sph: Number(rx.sph), cyl: rx.cyl, axis: rx.axis, add: rx.add };
			if (isNum(f.orSph) && Number(f.orSph) !== 0) out.sph = Store.snapClPower(Store.orderPowerFromTrial(rx.sph, f.orSph));
			if (isNum(rx.cyl) && Number(rx.cyl) !== 0 && isNum(rx.axis) && Number(f.rotation) > 0 && f.rotationDir) {
				const turned = Number(rx.axis) + (f.rotationDir === "left" ? 1 : -1) * Number(f.rotation);
				const axis = (((turned - 1) % 180) + 180) % 180 + 1;
				out.axis = exactAxis ? axis : Math.round(axis / 10) * 10 || 180;
			}
			return out;
		}

		// A draft's expiry starts as the usual term from the day it was started. Picked up on a later day -
		// a contact lens prescription waiting on its trial, say - the term runs from today instead, so it is
		// good for the full term once issued. A date the prescriber chose stays. Returns whether it moved.
		function restartExpiry(p) {
			const rx = p && rxOf(p);
			if (!isDraft(p) || !p.created || !rx.expires) return false;
			const usualFrom = (d) => moment(Store.addDays(d, VALID_DAYS[p.type])).format("YYYY-MM-DD");
			const started = usualFrom(p.created);
			if (moment(rx.expires).format("YYYY-MM-DD") !== started || usualFrom(new Date()) === started) return false;
			rx.expires = Store.addDays(new Date(), VALID_DAYS[p.type]);
			return true;
		}

		function emptyGlassesRx() {
			return {
				type: "glasses",
				id: null,
				ref: "New",
				created: new Date(),
				status: "DRAFT",
				issuedAt: null,
				rx: angular.extend(Store.emptyRx(), { expires: Store.addDays(new Date(), VALID_DAYS.glasses) }),
				recommendations: "",
				advice: "",
				patientNotes: "",
				sentTo: null,
			};
		}

		function emptyContactsRx() {
			const fitting = Store.emptyFitting();
			delete fitting.fee;
			return {
				type: "contacts",
				id: null,
				created: new Date(),
				status: "DRAFT",
				issuedAt: null,
				product: null,
				productOs: null,
				cl: angular.extend(Store.emptyClRx(), { expires: Store.addDays(new Date(), VALID_DAYS.contacts), specNotes: "" }),
				fitting: fitting,
				recommendations: "",
				advice: "",
				patientNotes: "",
				sentTo: null,
			};
		}

		// ------------------------------------------------------------------
		// As the API sends them and as the dialogs edit them
		// ------------------------------------------------------------------
		function fromApi(r) {
			const p = {
				id: r.prescription_id,
				patientId: r.patient_id,
				type: r.pathway === "CONTACTS" ? "contacts" : "glasses",
				created: r.date_created ? new Date(r.date_created) : null,
				status: r.status,
				issuedAt: r.date_issued ? new Date(r.date_issued) : null,
				issuedBy: r.issued_by_user_id || null,
				// The patient's copy the server filed in their documents when it was issued, or null.
				patientCopyDocumentId: r.patient_copy_document_id || null,
				sentTo: r.sent_to || null,
				recommendations: r.recommendations || "",
				advice: r.advice || "",
				patientNotes: r.patient_notes || "",
				withdrawn: r.status === "WITHDRAWN" ? { reason: r.withdrawn_reason || "", by: r.withdrawn_by_user_id || null, at: r.date_withdrawn ? new Date(r.date_withdrawn) : null } : null,
				// The practice it was issued at, as the patient's copy names it.
				practice: { name: r.practice_name || "", address: r.practice_address || "", phone: r.practice_phone || "", email: r.practice_email || "" },
			};
			if (p.type === "contacts") {
				const product = Store.productFromApi(r.contact_lens_rx && r.contact_lens_rx.product);
				const fitting = Store.fittingFromApi(r.fitting);
				delete fitting.fee;
				return angular.extend(p, {
					product: product,
					productOs: Store.productFromApi(r.contact_lens_rx && r.contact_lens_rx.left_product),
					cl: Store.contactLensRxFromApi(r.contact_lens_rx, !!(product && product.custom)),
					fitting: fitting,
				});
			}
			return angular.extend(p, { rx: Store.spectacleRxFromApi(r.spectacle_rx) });
		}

		function toApi(patientId, p) {
			const contacts = isContactsRx(p);
			return {
				patient_id: patientId,
				pathway: contacts ? "CONTACTS" : "GLASSES",
				spectacle_rx: contacts ? null : Store.spectacleRxToApi(p.rx),
				recommendations: p.recommendations || null,
				advice: p.advice || null,
				patient_notes: p.patientNotes || null,
				contact_lens_rx: contacts ? Store.contactLensRxToApi(p.cl, p.product, p.productOs) : null,
				fitting: contacts ? Store.fittingToApi(p.fitting) : null,
			};
		}

		// ------------------------------------------------------------------
		// Prescriptions (from the API, per patient)
		// ------------------------------------------------------------------
		// A patient's prescriptions, newest first. Kept in place, so a tile holding the list sees changes.
		const byPatient = {};
		const forPatient = (patientId) => byPatient[patientId] || (byPatient[patientId] = []);

		const newestFirst = (a, b) => new Date(b.issuedAt || b.created) - new Date(a.issuedAt || a.created);

		function upsert(patientId, p) {
			const list = forPatient(patientId);
			const i = list.findIndex((o) => o.id === p.id);
			if (i >= 0) list[i] = p;
			else list.unshift(p);
			list.sort(newestFirst);
			return p;
		}

		// Reads the patient's prescriptions. The catalogue comes first, so a contact lens prescription
		// carries the parameters its lens is made in.
		function load(patientId) {
			return Store.loadCatalogue()
				.catch(angular.noop)
				.then(() => LensPrescriptionAPI.getByPatient(patientId))
				.then(function (resp) {
					const list = forPatient(patientId);
					list.length = 0;
					(resp.data || []).forEach((r) => list.push(fromApi(r)));
					list.sort(newestFirst);
					return list;
				});
		}

		function find(patientId, id) {
			return id ? forPatient(patientId).find((p) => p.id === id) || null : null;
		}

		// Current is the newest issued prescription of its kind that is still in date; an older one in
		// date is superseded. A withdrawn one never counts.
		function statusOf(patientId, p) {
			if (isWithdrawn(p)) return STATUS.WITHDRAWN;
			if (!isIssued(p)) return onTrial(p) ? STATUS.TRIAL : STATUS.DRAFT;
			if (isExpired(p)) return STATUS.EXPIRED;
			const newer = forPatient(patientId).some((o) => o !== p && o.id !== p.id && o.type === p.type && isIssued(o) && new Date(o.issuedAt) > new Date(p.issuedAt));
			return newer ? STATUS.SUPERSEDED : STATUS.CURRENT;
		}

		// Saves a draft, creating it the first time. Resolves to the draft as the server keeps it.
		function save(patientId, p) {
			const body = toApi(patientId, p);
			const request = p.id ? LensPrescriptionAPI.update(p.id, body) : LensPrescriptionAPI.create(body);
			return request.then((resp) => upsert(patientId, fromApi(resp.data)));
		}

		// Saves the draft, then issues it - the server files the patient's copy in their documents as it
		// does. Resolves to the issued prescription.
		function issue(patientId, p, sentTo) {
			return save(patientId, p)
				.then((saved) => LensPrescriptionAPI.issue(saved.id, sentTo))
				.then(function (resp) {
					const issued = upsert(patientId, fromApi(resp.data));
					if (copyFiled(issued)) $rootScope.$broadcast("lensPrescribing:document", patientId);
					return issued;
				});
		}

		// Withdraws an issued prescription found to be wrong. It is kept, with the reason, and can't be
		// dispensed any more. Resolves to the withdrawn prescription.
		function withdraw(patientId, p, reason) {
			return LensPrescriptionAPI.withdraw(p.id, reason).then((resp) => upsert(patientId, fromApi(resp.data)));
		}

		// A new draft that repeats an issued prescription - the same lens and powers, a new expiry - for
		// the prescriber to check against today's findings and issue. A trial isn't repeated.
		function renewal(p) {
			const fresh = isContactsRx(p) ? emptyContactsRx() : emptyGlassesRx();
			const from = `Renewed from the ${moment(p.issuedAt || p.created).format("D MMM YYYY")} prescription`;
			if (isContactsRx(p)) {
				angular.extend(fresh.cl, angular.copy({ od: p.cl.od, os: p.cl.os, specNotes: p.cl.specNotes }), { source: from });
				fresh.product = angular.copy(p.product);
				fresh.productOs = angular.copy(p.productOs || null);
				fresh.fitting.wear = p.fitting ? p.fitting.wear : fresh.fitting.wear;
				fresh.fitting.notes = p.fitting ? p.fitting.notes : "";
			} else {
				["od", "os"].forEach((k) => RX_FIELDS.concat(["pd"]).forEach((f) => (fresh.rx[k][f] = angular.copy(p.rx[k][f]))));
				angular.extend(fresh.rx, { pd: p.rx.pd, use: p.rx.use, source: from });
				fresh.recommendations = p.recommendations || "";
			}
			fresh.advice = p.advice || "";
			fresh.patientNotes = p.patientNotes || "";
			return fresh;
		}

		// The patient's earlier prescriptions of a kind that stand or have stood - issued, whether still
		// current, superseded or expired, never withdrawn - newest first, leaving out `p` itself.
		const earlier = (patientId, type, p) =>
			forPatient(patientId).filter((o) => o.type === type && isIssued(o) && (!p || (o.id !== p.id && (!p.issuedAt || new Date(o.issuedAt) < new Date(p.issuedAt)))));

		// The prescription a new one is compared with: the last of its kind before it.
		const previousOf = (patientId, p) => earlier(patientId, p.type, p)[0] || null;

		// The contact lens the patient wears now: the lens of their latest contact lens prescription, or of
		// the latest contact lens job collected. { product, productOs } or null.
		function currentLens(patientId) {
			const rx = earlier(patientId, "contacts", null)[0];
			if (rx && rx.product && !rx.product.madeToOrder) return { product: rx.product, productOs: rx.productOs || null };
			const collected = Store.getJobs(patientId)
				.filter((j) => Store.isContacts(j) && j.status === "COLLECTED" && j.product && !j.product.madeToOrder)
				.sort((a, b) => new Date((b.order && b.order.collectedAt) || b.created) - new Date((a.order && a.order.collectedAt) || a.created))[0];
			return collected ? { product: collected.product, productOs: collected.productOs || null } : null;
		}

		// ------------------------------------------------------------------
		// Checking a new Rx against the last one
		// ------------------------------------------------------------------
		// How far each eye's powers moved since the last prescription: { od, os } lines like "SPH -0.50,
		// CYL no change", or null where the eye didn't change. Compared in minus cylinder.
		function changesFrom(prev, p) {
			if (!prev || !p) return null;
			const out = {};
			const a = rxOf(prev);
			const b = rxOf(p);
			["od", "os"].forEach(function (k) {
				const before = Store.toMinusCyl(a[k] || {});
				const now = Store.toMinusCyl(b[k] || {});
				if (before.balance || now.balance || before.noLens || now.noLens || !isNum(before.sph) || !isNum(now.sph)) return (out[k] = null);
				const parts = [];
				const diff = (x, y) => Math.round(((Number(y) || 0) - (Number(x) || 0)) * 100) / 100;
				const dSph = diff(before.sph, now.sph);
				const dCyl = diff(before.cyl, now.cyl);
				if (dSph) parts.push(`SPH ${Store.fmtPower(dSph)}`);
				if (dCyl) parts.push(`CYL ${Store.fmtPower(dCyl)}`);
				const cyls = Math.abs(Number(now.cyl) || 0) >= 0.5 && Math.abs(Number(before.cyl) || 0) >= 0.5;
				const dAxis = cyls ? axisDiff(before.axis, now.axis) : 0;
				if (dAxis) parts.push(`axis ${dAxis}°`);
				const dAdd = isNum(before.add) && isNum(now.add) ? diff(before.add, now.add) : 0;
				if (dAdd) parts.push(`ADD ${Store.fmtPower(dAdd)}`);
				out[k] = parts.length ? { text: parts.join(", "), sph: dSph, cyl: dCyl, axis: dAxis, cylNow: Math.abs(Number(now.cyl) || 0) } : null;
			});
			return out;
		}

		// The shorter way round between two axes, 0 to 90 degrees.
		function axisDiff(a, b) {
			if (!isNum(a) || !isNum(b)) return 0;
			const d = Math.abs(Number(a) - Number(b)) % 180;
			return Math.min(d, 180 - d);
		}

		// What looks like a slip of the keyboard, or a change big enough to check before it is issued.
		// Warnings, never refusals: a strong or changed Rx can be right.
		function rxWarnings(p, prev) {
			const out = [];
			const rx = rxOf(p);
			const names = { od: "Right", os: "Left" };
			["od", "os"].forEach(function (k) {
				const e = rx[k] || {};
				if (e.balance || e.noLens) return;
				if (isNum(e.sph) && Math.abs(Number(e.sph)) > 20) out.push(`${names[k]} sphere ${Store.fmtPower(e.sph)} is unusually strong.`);
				if (isNum(e.cyl) && Math.abs(Number(e.cyl)) > 6) out.push(`${names[k]} cylinder ${Store.fmtPower(e.cyl)} is unusually strong.`);
			});
			const live = ["od", "os"].filter((k) => rx[k] && !rx[k].balance && !rx[k].noLens && isNum(rx[k].sph));
			if (live.length === 2 && isNum(rx.od.add) && isNum(rx.os.add) && Math.abs(Number(rx.od.add) - Number(rx.os.add)) > 0.5) {
				out.push(`The ADDs differ between the eyes (R ${Store.fmtPower(rx.od.add)}, L ${Store.fmtPower(rx.os.add)}).`);
			}
			const changes = changesFrom(prev, p);
			if (changes) {
				["od", "os"].forEach(function (k) {
					const c = changes[k];
					if (!c) return;
					// An axis turning matters once there is a dioptre of cylinder or more.
					if (Math.abs(c.sph) > 2 || Math.abs(c.cyl) > 1 || (c.axis > 20 && c.cylNow >= 1)) {
						out.push(`${names[k]}: a big change since ${moment(prev.issuedAt).format("D MMM YYYY")} (${c.text}).`);
					}
				});
			}
			return out;
		}

		// ------------------------------------------------------------------
		// The patient's copy
		// ------------------------------------------------------------------
		// The server makes the patient's copy (with its FreeMarker template) and files one in the patient's
		// documents when the prescription is issued. A copy asked for later is made fresh: marked expired or
		// withdrawn, and signed by the prescriber, on their behalf by another registered practitioner, or not
		// at all - a copy from the practice's records - depending on who asks.
		const myName = () => [Session.user && Session.user.firstName, Session.user && Session.user.lastName].filter(Boolean).join(" ");
		const myRegistration = () => (Session.user && Session.user.registration && Session.user.registration.number) || null;

		// Opens the patient's copy in a new tab, to print or download. The tab is opened in the click itself,
		// so the browser doesn't take it for a pop-up, and given the PDF once the server has made it.
		function openPatientCopy(p) {
			const tab = window.open("", "_blank");
			return LensPrescriptionAPI.getPatientCopy(p.id).then(
				function (resp) {
					const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
					if (tab) tab.location.href = url;
					else window.open(url, "_blank");
					// The new tab has loaded it well before this; the URL only has to outlive that.
					setTimeout(() => URL.revokeObjectURL(url), 60000);
				},
				function (resp) {
					if (tab) tab.close();
					return $q.reject(resp);
				}
			);
		}

		// Whether the copy filed when it was issued made it to the patient's documents.
		const copyFiled = (p) => !!(p && p.patientCopyDocumentId);

		function remove(patientId, p) {
			return LensPrescriptionAPI.remove(p.id).then(function () {
				const list = forPatient(patientId);
				const i = list.findIndex((o) => o.id === p.id);
				if (i >= 0) list.splice(i, 1);
			});
		}

		// ------------------------------------------------------------------
		// Into dispensing
		// ------------------------------------------------------------------
		// Copy a prescription onto a dispensing job - what the server will copy when the job is saved,
		// so the dialog shows it straight away. A glasses job keeps its own PD and heights, starting from
		// the prescriber's where it has none; a contact lens job keeps its fitting fee, which is billing
		// and so belongs to dispensing.
		const sourceOf = (p) => `${title(p)} prescription, ${moment(p.issuedAt || p.created).format("D MMM YYYY")}`;

		// A binocular PD isn't split into the job's monocular PDs: the dispenser measures those.
		function applyToJob(job, p) {
			if (isContactsRx(p)) {
				job.product = angular.copy(p.product);
				job.productOs = angular.copy(p.productOs || null);
				job.cl = angular.extend(angular.copy(p.cl), { source: sourceOf(p) });
				job.fitting = angular.extend(angular.copy(p.fitting), { fee: job.fitting ? job.fitting.fee : "initial" });
			} else {
				["od", "os"].forEach(function (k) {
					RX_FIELDS.forEach((f) => (job.rx[k][f] = p.rx[k][f]));
					MEASURE_FIELDS.forEach(function (f) {
						if (!isNum(job.rx[k][f]) && isNum(p.rx[k][f])) job.rx[k][f] = p.rx[k][f];
					});
				});
				angular.extend(job.rx, {
					pd: p.rx.pd, use: p.rx.use, expires: p.rx.expires, prescriber: p.rx.prescriber, prescriberRegistration: p.rx.prescriberRegistration, source: sourceOf(p),
				});
				job.use = p.rx.use;
			}
			job.rxId = p.id;
			return job;
		}

		// A glasses job's copy of its prescription brought back in line with it - an issued prescription
		// never changes, so this only repairs a stale copy (an ADD missing, say). What the glasses are for
		// (job.use) is the job's own and stays. Returns whether anything was put right.
		function refreshRx(job, p) {
			if (!job || !p || isContactsRx(p) || !job.rx) return false;
			let changed = false;
			["od", "os"].forEach((k) => RX_FIELDS.forEach(function (f) {
				const want = p.rx[k][f] === undefined ? null : p.rx[k][f];
				if ((job.rx[k][f] === undefined ? null : job.rx[k][f]) !== want) {
					job.rx[k][f] = want;
					changed = true;
				}
			}));
			if (job.rx.use !== p.rx.use) {
				job.rx.use = p.rx.use;
				changed = true;
			}
			return changed;
		}

		// An earlier spectacle prescription's powers, copied onto one being written. Its PD comes too
		// where none has been measured.
		function copyRx(rx, from) {
			["od", "os"].forEach((k) => RX_FIELDS.forEach((f) => (rx[k][f] = angular.copy(from.rx[k][f]))));
			if (!isNum(rx.pd) && isNum(from.rx.pd)) rx.pd = from.rx.pd;
			rx.use = from.rx.use;
			rx.source = `Copied from the ${moment(from.issuedAt || from.created).format("D MMM YYYY")} prescription`;
		}

		// An earlier contact lens prescription's lens, powers and wear, copied onto one being written.
		function copyContactRx(p, from) {
			p.product = angular.copy(from.product);
			p.productOs = angular.copy(from.productOs || null);
			["od", "os"].forEach((k) => (p.cl[k] = angular.copy(from.cl[k])));
			p.cl.specNotes = from.cl.specNotes || "";
			if (from.fitting) p.fitting.wear = from.fitting.wear;
			p.cl.source = `Copied from the ${moment(from.issuedAt || from.created).format("D MMM YYYY")} prescription`;
		}

		// A new dispensing job made from a prescription, ready to open in the job dialog. It is a
		// draft until it is saved, so the dialog treats it as new.
		const newJob = (p) => applyToJob(Store.newJob(isContactsRx(p) ? "contacts" : "glasses"), p);

		// One line for a prescription, the way it reads on the tile and in the dispensing picker.
		function summary(p) {
			const eye = (e) => {
				if (e && e.noLens) return "No lens";
				// A balance eye keeps whatever powers are written, so they are shown with the flag.
				if (!e || !isNum(e.sph)) return e && e.balance ? "Balance" : "-";
				let s = Store.fmtPower(e.sph);
				if (isNum(e.cyl) && Number(e.cyl) !== 0) s += ` / ${Store.fmtPower(e.cyl)} × ${e.axis || "?"}`;
				else if (!isContactsRx(p)) s += " DS";
				if (e.intAdd) s += ` Int ${Store.fmtPower(e.intAdd)}`;
				if (e.add) s += ` Add ${typeof e.add === "number" ? Store.fmtPower(e.add) : e.add}`;
				const prism = isContactsRx(p) ? "" : Store.fmtPrism(e);
				if (prism) s += ` ${prism}`;
				return e.balance ? `Balance (${s})` : s;
			};
			const rx = rxOf(p);
			return `R ${eye(rx.od)}  ·  L ${eye(rx.os)}`;
		}

		const USES = [
			{ key: "distance", label: "Distance", title: "Distance glasses", icon: "fa-road", design: "single" },
			{ key: "near", label: "Reading", title: "Reading glasses", icon: "fa-book", design: "single" },
			{ key: "intermediate", label: "Computer", title: "Computer glasses", icon: "fa-desktop", design: "office" },
			{ key: "multifocal", label: "All distances", title: "Multifocal glasses", icon: "fa-arrows-v", design: "progressive" },
		];
		const useMeta = (key) => USES.find((u) => u.key === key) || USES[0];

		// A glasses job by what its prescription is for - "Reading glasses" - or plain "Glasses" before it has one.
		const glassesKind = (job) => (job && job.rxId && job.use ? useMeta(job.use).title : "Glasses");

		// What a prescription is called on screen: what it is for, or the lens it is for - both lenses when
		// the eyes wear different ones. Ids stay internal. Works for a contact lens job too.
		function title(p) {
			if (!p) return "";
			const name = (l) => `${l.brand || ""} ${l.name || ""}`.trim();
			if (isContactsRx(p)) {
				if (!p.product) return "Contact lenses";
				return p.productOs ? `R ${name(p.product)} · L ${name(p.productOs)}` : name(p.product);
			}
			return useMeta(p.rx && p.rx.use).title;
		}

		const service = {
			STATUS,
			USES,
			VALID_DAYS,
			useMeta,
			title,
			glassesKind,
			refreshRx,
			isContactsRx,
			isIssued,
			isWithdrawn,
			isDraft,
			isExpired,
			isUsable,
			TRIAL_OUTCOMES,
			TRIAL_FIT,
			outcomeLabel,
			fitLabel,
			onTrial,
			currentTrial,
			newTrial,
			adjustedRx,
			restartExpiry,
			expiresOf,
			hasPowers,
			emptyGlassesRx,
			emptyContactsRx,
			load,
			forPatient,
			find,
			statusOf,
			save,
			issue,
			withdraw,
			renewal,
			previousOf,
			currentLens,
			changesFrom,
			rxWarnings,
			remove,
			openPatientCopy,
			copyFiled,
			applyToJob,
			copyRx,
			copyContactRx,
			newJob,
			summary,
			myName,
			myRegistration,

			// Whether the signed-in user may open the patient's copy, which the server makes for them.
			canViewCopy: () => Session.hasPermission("CLINICAL_LENS_PRESCRIPTION_COPY_VIEW"),

			// Whether the signed-in user may withdraw an issued prescription.
			canWithdraw: () => Session.hasPermission("CLINICAL_LENS_PRESCRIPTION_WITHDRAW"),

			// What a dispensing job of this kind can be made from, newest first.
			usable: (patientId, type) => forPatient(patientId).filter((p) => p.type === type && isUsable(p)),

			// The dispensing jobs made from a prescription, of those loaded for the patient.
			jobsFor: (patientId, p) => Store.getJobs(patientId).filter((j) => j.rxId === p.id && j.status !== "DRAFT"),

			// The dialog that writes and shows a prescription of this kind.
			dialogFor: function (p, type) {
				return (p ? isContactsRx(p) : type === "contacts")
					? { controller: "lensRxContactsModalController", templateUrl: "app/src/js/components/lensPrescribing/lens-rx-contacts-modal.tmpl.html" }
					: { controller: "lensRxGlassesModalController", templateUrl: "app/src/js/components/lensPrescribing/lens-rx-glasses-modal.tmpl.html" };
			},

			// The dialog that sends a contact lens prescription's trial pair home and records the review.
			trialDialog: { controller: "lensRxTrialModalController", templateUrl: "app/src/js/components/lensPrescribing/lens-rx-trial-modal.tmpl.html" },

			// The dialog that withdraws an issued prescription, which is locked and never opened again.
			withdrawDialog: { controller: "lensRxWithdrawModalController", templateUrl: "app/src/js/components/lensPrescribing/lens-rx-withdraw-modal.tmpl.html" },
		};

		return service;
	},
]);
